import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** One column's identity and its size bounds. */
export interface ResizableColumn {
    /**
     * Stable identity used as the storage key. Use the column's MEANING
     * (`code`, `label`, a role id) — never its index, or inserting a column
     * silently reassigns every stored width to the wrong column.
     */
    key: string;
    /** Width in px when the user has not resized it. */
    width: number;
    /** Floor, px. Defaults to 64 — narrow enough to hide a column's content,
     *  wide enough that the grip stays grabbable. */
    min?: number;
    /** Ceiling, px. Defaults to 960. */
    max?: number;
    /** Set for a column that must not be resized (an icon or checkbox rail). */
    fixed?: boolean;
}

export interface UseResizableColumnsOptions {
    /**
     * Namespaced under `ui:colwidths:`. Distinct per TABLE, not per screen — two
     * tables on one screen with the same key would fight over one entry.
     */
    storageKey: string;
    columns: ResizableColumn[];
    /**
     * Bump when the column set changes shape enough that stored widths would be
     * wrong (a column split in two, say). Stored payloads at another version are
     * discarded rather than migrated.
     */
    version?: number;
    /** Escape hatch for tests / SSR. Defaults to `window.localStorage`. */
    storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

export interface UseResizableColumnsResult {
    /** Effective width per key — stored value when present, else the default. */
    widths: Record<string, number>;
    /** True when at least one column differs from its default. */
    isCustomised: boolean;
    /** Begin a drag. Pass the pointer event from the grip's `onPointerDown`. */
    startResize: (key: string, event: React.PointerEvent) => void;
    /** Nudge one column, for keyboard operation of the grip. */
    nudge: (key: string, deltaPx: number) => void;
    /** Forget every stored width for this table. */
    reset: () => void;
    /** The key currently being dragged, for a caller that wants to highlight it. */
    resizingKey: string | null;
}

const PREFIX = 'ui:colwidths:';
const DEFAULT_MIN = 64;
const DEFAULT_MAX = 960;

interface StoredPayload {
    v: number;
    w: Record<string, number>;
}

function defaultStorage(): UseResizableColumnsOptions['storage'] {
    // Guarded rather than assumed: `localStorage` throws on access in Safari
    // private mode and does not exist during SSR, and a table that cannot render
    // because a preference could not be read is a bad trade.
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
}

/**
 * Column widths a user can drag, remembered across reloads.
 *
 * HEADLESS ON PURPOSE. It returns numbers and handlers; the caller applies them
 * however its table is built (a `<col>` element, an inline `width`, a grid
 * template). Tier-1 ships no CSS, and a table primitive that owned its own
 * markup could not serve both the plain `<table>`s in the console and antd's
 * `Table` — so this owns the STATE and leaves the rendering alone.
 *
 * Persistence rules that matter:
 *
 * * **Unknown keys in storage are ignored, not merged.** A column that was
 *   renamed or removed leaves an orphan entry behind; reading it back would
 *   apply a width to nothing, and writing it forward would grow the payload
 *   forever.
 * * **Writes happen on pointer-up, not per mousemove.** A drag fires dozens of
 *   moves a second and `localStorage` is synchronous — writing per frame stalls
 *   the drag it is trying to record.
 * * **A failed read or write is silent.** The feature is a convenience; losing
 *   it must never take the table down with it.
 */
export function useResizableColumns({
    storageKey,
    columns,
    version = 1,
    storage,
}: UseResizableColumnsOptions): UseResizableColumnsResult {
    const store = storage === undefined ? defaultStorage() : storage;
    const fullKey = PREFIX + storageKey;

    const bounds = useMemo(() => {
        const map = new Map<string, { min: number; max: number; def: number; fixed: boolean }>();
        for (const c of columns) {
            map.set(c.key, {
                min: c.min ?? DEFAULT_MIN,
                max: c.max ?? DEFAULT_MAX,
                def: c.width,
                fixed: Boolean(c.fixed),
            });
        }
        return map;
    }, [columns]);

    const [overrides, setOverrides] = useState<Record<string, number>>({});
    const [resizingKey, setResizingKey] = useState<string | null>(null);

    // Read once per storage key. Deliberately not re-read on every `columns`
    // change: the user's drags live in state after mount, and re-reading would
    // discard an in-flight resize whenever the column list re-renders.
    useEffect(() => {
        if (!store) return;
        let raw: string | null = null;
        try {
            raw = store.getItem(fullKey);
        } catch {
            return;
        }
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw) as StoredPayload;
            if (!parsed || parsed.v !== version || typeof parsed.w !== 'object') {
                return;
            }
            setOverrides(parsed.w ?? {});
        } catch {
            // Corrupt entry — drop it rather than fail on every mount hereafter.
            try {
                store.removeItem(fullKey);
            } catch {
                /* nothing more to do */
            }
        }
    }, [fullKey, version, store]);

    /** Stored widths are filtered against the LIVE column set on every read. */
    const widths = useMemo(() => {
        const out: Record<string, number> = {};
        for (const c of columns) {
            const b = bounds.get(c.key)!;
            const stored = c.fixed ? undefined : overrides[c.key];
            out[c.key] = stored === undefined ? c.width : clamp(stored, b.min, b.max);
        }
        return out;
    }, [columns, overrides, bounds]);

    const isCustomised = useMemo(
        () => columns.some((c) => !c.fixed && overrides[c.key] !== undefined),
        [columns, overrides]
    );

    // Written from the pointer handlers, which must not re-subscribe per move.
    const latest = useRef(overrides);
    latest.current = overrides;

    const persist = useCallback(
        (next: Record<string, number>) => {
            if (!store) return;
            // Only keys the caller still declares — see the docstring.
            const live: Record<string, number> = {};
            for (const c of columns) {
                if (!c.fixed && next[c.key] !== undefined) live[c.key] = next[c.key];
            }
            try {
                if (Object.keys(live).length === 0) store.removeItem(fullKey);
                else store.setItem(fullKey, JSON.stringify({ v: version, w: live }));
            } catch {
                /* quota or private mode — the widths still work for this session */
            }
        },
        [columns, fullKey, version, store]
    );

    const startResize = useCallback(
        (key: string, event: React.PointerEvent) => {
            const b = bounds.get(key);
            if (!b || b.fixed) return;
            event.preventDefault();
            event.stopPropagation();

            const startX = event.clientX;
            const startWidth = latest.current[key] ?? b.def;
            setResizingKey(key);

            // Suppressed for the duration of the drag. Without it the browser
            // range-selects the header text under the cursor, which is what a
            // resize attempt looks like on a table that has no grip.
            const body = document.body;
            const prevSelect = body.style.userSelect;
            const prevCursor = body.style.cursor;
            body.style.userSelect = 'none';
            body.style.cursor = 'col-resize';

            function onMove(e: PointerEvent) {
                const next = clamp(startWidth + (e.clientX - startX), b!.min, b!.max);
                setOverrides((prev) => (prev[key] === next ? prev : { ...prev, [key]: next }));
            }
            function onUp() {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                window.removeEventListener('pointercancel', onUp);
                body.style.userSelect = prevSelect;
                body.style.cursor = prevCursor;
                setResizingKey(null);
                persist(latest.current);
            }
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
        },
        [bounds, persist]
    );

    /** Keyboard path. A grip that only responds to a drag is unusable without a mouse. */
    const nudge = useCallback(
        (key: string, deltaPx: number) => {
            const b = bounds.get(key);
            if (!b || b.fixed) return;
            setOverrides((prev) => {
                const next = {
                    ...prev,
                    [key]: clamp((prev[key] ?? b.def) + deltaPx, b.min, b.max),
                };
                persist(next);
                return next;
            });
        },
        [bounds, persist]
    );

    const reset = useCallback(() => {
        setOverrides({});
        if (!store) return;
        try {
            store.removeItem(fullKey);
        } catch {
            /* nothing more to do */
        }
    }, [fullKey, store]);

    return { widths, isCustomised, startResize, nudge, reset, resizingKey };
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, Math.round(value)));
}
